import { api } from './index';

export interface PulseAdminReportSummary {
  id: string;
  status: 'active' | 'hidden';
  formType: string;
  submittedAt: string;
  memberId: string;
  memberName: string;
  memberEmail?: string | null;
  sourceVersion?: string | null;
  sourceCount: number;
}

export interface PulseAdminReportSource {
  id: string;
  formResponseId: string;
  role: 'base' | 'overlay';
  isAnchor: boolean;
  precedence: number;
  formType: string;
  typeformFormId: string;
  respondentEmail?: string | null;
  respondentFirstName?: string | null;
  respondentLastName?: string | null;
  respondentCompany?: string | null;
  linkedPersonId?: string | null;
  status: string;
  submittedAt?: string | null;
}

export interface PulseAdminOverrideOptionSection {
  key: string;
  title: string;
}

export interface PulseAdminOverrideOptionQuestion {
  key: string;
  sectionKey: string;
  label: string;
}

export interface PulseAdminReportOverrides {
  hiddenSections?: string[];
  hiddenQuestions?: string[];
}

export interface PulseAdminReportDetail extends PulseAdminReportSummary {
  overrides: PulseAdminReportOverrides;
  metadata?: Record<string, unknown> | null;
  sections: Array<Record<string, unknown>>;
  sources: PulseAdminReportSource[];
  overrideOptions: {
    sections: PulseAdminOverrideOptionSection[];
    questions: PulseAdminOverrideOptionQuestion[];
  };
  debrief?: {
    debriefedAt?: string | null;
    debriefedById?: string | null;
    debriefedByName?: string | null;
  } | null;
}

export interface PulseAdminReportsResponse {
  reports: PulseAdminReportSummary[];
  total: number;
  page: number;
  per: number;
}

export interface GetPulseAdminReportsParams {
  page?: number;
  per?: number;
}

export const pulseAdminApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPulseAdminReports: builder.query<
      PulseAdminReportsResponse,
      GetPulseAdminReportsParams | void
    >({
      providesTags: (result) => [
        'PulseReport',
        ...(result?.reports.map(({ id }) => ({
          type: 'PulseReport' as const,
          id,
        })) ?? []),
      ],
      query: (params = {}) => ({
        method: 'GET',
        params,
        url: '/admin/pulse/reports',
      }),
    }),
    getPulseAdminReport: builder.query<PulseAdminReportDetail, string>({
      providesTags: (_result, _error, id) => [
        { type: 'PulseReport' as const, id },
      ],
      query: (id) => ({
        method: 'GET',
        url: `/admin/pulse/reports/${id}`,
      }),
      transformResponse: (response: { report: PulseAdminReportDetail }) =>
        response.report,
    }),
    updatePulseAdminReportStatus: builder.mutation<
      PulseAdminReportDetail,
      { id: string; status: 'active' | 'hidden' }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PulseReport' as const, id },
        'PulseReport',
      ],
      query: ({ id, status }) => ({
        method: 'PATCH',
        data: { status },
        url: `/admin/pulse/reports/${id}/status`,
      }),
      transformResponse: (response: { report: PulseAdminReportDetail }) =>
        response.report,
    }),
    updatePulseAdminReportOverrides: builder.mutation<
      PulseAdminReportDetail,
      { id: string; overrides: PulseAdminReportOverrides }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PulseReport' as const, id },
        'PulseReport',
      ],
      query: ({ id, overrides }) => ({
        method: 'PATCH',
        data: { overrides },
        url: `/admin/pulse/reports/${id}/overrides`,
      }),
      transformResponse: (response: { report: PulseAdminReportDetail }) =>
        response.report,
    }),
    attachPulseAdminReportSource: builder.mutation<
      PulseAdminReportDetail,
      { id: string; formResponseId: string }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PulseReport' as const, id },
        'PulseReport',
      ],
      query: ({ id, formResponseId }) => ({
        method: 'POST',
        data: { formResponseId },
        url: `/admin/pulse/reports/${id}/sources`,
      }),
      transformResponse: (response: { report: PulseAdminReportDetail }) =>
        response.report,
    }),
    rebuildPulseAdminReport: builder.mutation<PulseAdminReportDetail, string>({
      invalidatesTags: (_result, _error, id) => [
        { type: 'PulseReport' as const, id },
        'PulseReport',
      ],
      query: (id) => ({
        method: 'POST',
        url: `/admin/pulse/reports/${id}/rebuild`,
      }),
      transformResponse: (response: { report: PulseAdminReportDetail }) =>
        response.report,
    }),
  }),
});

export const {
  useAttachPulseAdminReportSourceMutation,
  useGetPulseAdminReportQuery,
  useGetPulseAdminReportsQuery,
  useRebuildPulseAdminReportMutation,
  useUpdatePulseAdminReportOverridesMutation,
  useUpdatePulseAdminReportStatusMutation,
} = pulseAdminApi;
